import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BrandMark } from "./BrandMark";
import { FadeUp, MaskLines, SectionLabel } from "./Reveal";
import { ALL_BRANDS, EURO_BRANDS, FEATURED_NAMES } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const FEATURED = EURO_BRANDS.filter((b) => FEATURED_NAMES.includes(b.name));
const REST = ALL_BRANDS.filter((b) => !FEATURED_NAMES.includes(b.name));

const LogoTile = ({ b, featured }) => (
  <div data-testid={`brand-tile-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`group relative flex aspect-square flex-col items-center justify-center gap-3 border-b border-r border-white/10 transition-colors duration-500 hover:bg-white/[0.03] ${featured ? "text-bone/90" : "text-bone/90"} hover:text-brand`}>
    <BrandMark brand={b} className="h-12 w-20 transition-transform duration-500 group-hover:scale-110 md:h-14 md:w-24" />
    <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-zinc-300 transition-colors group-hover:text-bone">{b.name}</span>
  </div>
);

export const European = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const { t } = useLang();
  const e = t.european;

  return (
    <section id="european" ref={ref} data-testid="european-section" className="relative overflow-hidden bg-coal py-24 md:py-36">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionLabel n="02">{e.label}</SectionLabel>
            <h2 className="font-display mt-8 text-4xl font-black uppercase leading-[0.92] text-bone sm:text-5xl lg:text-6xl">
              <MaskLines inView lines={[e.l1, e.l2, <span key="c" className="font-serif-i normal-case text-brand">{e.l3}</span>]} />
            </h2>
            <FadeUp>
              <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-300 md:text-lg">
                {e.body}
              </p>
            </FadeUp>
            <div className="relative mt-10 overflow-hidden chamfer">
              <motion.img style={{ y, scale: 1.12 }} src="/img/european.jpg" alt={e.imgAlt} loading="lazy" data-testid="european-image" className="aspect-[4/5] w-full object-cover object-center" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <FadeUp>
              <div className="flex items-baseline justify-between border-b border-white/10 pb-4">
                <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-brand">{e.focus}</span>
                <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-zinc-400">{FEATURED.length} {e.marques}</span>
              </div>
              <div className="grid grid-cols-2 border-l border-t border-white/10 sm:grid-cols-4">
                {FEATURED.map((b) => <LogoTile key={b.name} b={b} featured />)}
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-12 flex items-baseline justify-between border-b border-white/10 pb-4">
                <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-bone">{e.allMakes}</span>
                <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-zinc-400">{e.more}</span>
              </div>
              <div className="grid grid-cols-3 border-l border-t border-white/10 sm:grid-cols-5 lg:grid-cols-6">
                {REST.map((b) => <LogoTile key={b.name} b={b} />)}
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
